import React, { useState } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import procurementDocumentsService from '../../../services/procurementDocumentsService';
import VendorPicker from './VendorPicker';
import DocumentDownloadButtons from './DocumentDownloadButtons';
import RfqDocumentView from './RfqDocumentView';

const emptyItem = () => ({ description: '', specification: '', qty: '', unit: '', unit_price: '', total_price: '' });

const RfqFormModal = ({ purchaseRequest, onClose }) => {
  const [form, setForm] = useState({
    issue_date: new Date().toISOString().slice(0, 10),
    issuing_company: 'Puffin Group Ltd',
    buyer_contact_name: '',
    buyer_contact_email_phone: '',
    submission_deadline: '',
    delivery_location: '',
    delivery_completion_date: '',
    payment_terms_requested: '',
    validity_period_required: '',
    vendor_company_name: '',
    vendor_contact_person: '',
    vendor_email: '',
    vendor_phone: '',
    vendor_address: '',
    vendor_tax_reg_no: '',
    freight_shipping_cost: '',
    taxes_duties_included: null,
    warranty_period: '',
    lead_time: '',
    payment_terms_offered: '',
    quote_valid_until: '',
    supplier_id: null,
  });
  const [items, setItems] = useState([emptyItem()]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);
  const [showView, setShowView] = useState(false);

  const set = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  const onVendorSelect = ({ vendor }) => {
    if (!vendor) return;
    setForm((p) => ({
      ...p,
      supplier_id: vendor.id,
      vendor_company_name: vendor.company_name || '',
      vendor_contact_person: vendor.contact_person || '',
      vendor_email: vendor.email || '',
      vendor_phone: vendor.phone || '',
      vendor_address: (vendor.address && typeof vendor.address === 'object' ? vendor.address.text : vendor.address) || '',
      vendor_tax_reg_no: vendor.tax_number || '',
    }));
  };

  const updateItem = (index, field, value) => {
    setItems((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      if (field === 'qty' || field === 'unit_price') {
        const qty = Number(next[index].qty) || 0;
        const price = Number(next[index].unit_price) || 0;
        next[index].total_price = qty * price;
      }
      return next;
    });
  };

  const grandTotal = items.reduce((sum, it) => sum + (Number(it.total_price) || 0), 0);

  const handleSave = async () => {
    if (!items.some((it) => it.description.trim())) {
      toast.error('Add at least one item');
      return;
    }
    try {
      setSaving(true);
      const res = await procurementDocumentsService.createRfq({
        purchase_request_id: purchaseRequest.id,
        ...form,
        items: items.filter((it) => it.description.trim()),
        grand_total: grandTotal,
      });
      setSaved(res.data.data.rfq);
      toast.success('RFQ created');
    } catch (error) {
      console.error('Create RFQ error:', error);
      toast.error(error.response?.data?.message || 'Failed to create RFQ');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[60]">
      <div className="relative top-6 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Request for Quotation - {purchaseRequest.request_id}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {saved ? (
          <div className="text-center py-8">
            <p className="text-green-700 font-medium mb-1">RFQ {saved.rfq_number} created</p>
            <p className="text-sm text-gray-500 mb-4">Download the document below.</p>
            <div className="flex justify-center">
              <DocumentDownloadButtons
                onView={() => setShowView(true)}
                onDownloadDocx={() => procurementDocumentsService.downloadRfqDocx(saved.id, `${saved.rfq_number}.docx`)}
              />
            </div>
            <button onClick={onClose} className="mt-4 text-sm text-gray-500 hover:text-gray-700">
              Close
            </button>
            {showView && <RfqDocumentView rfq={saved} onClose={() => setShowView(false)} />}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Issue Date</label>
                <input type="date" value={form.issue_date} onChange={(e) => set('issue_date', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Submission Deadline</label>
                <input type="date" value={form.submission_deadline} onChange={(e) => set('submission_deadline', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Delivery / Completion Date</label>
                <input type="date" value={form.delivery_completion_date} onChange={(e) => set('delivery_completion_date', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Buyer / Contact Person</label>
                <input value={form.buyer_contact_name} onChange={(e) => set('buyer_contact_name', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Contact Email / Phone</label>
                <input value={form.buyer_contact_email_phone} onChange={(e) => set('buyer_contact_email_phone', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Delivery Location</label>
                <input value={form.delivery_location} onChange={(e) => set('delivery_location', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Payment Terms Requested</label>
                <input value={form.payment_terms_requested} onChange={(e) => set('payment_terms_requested', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Validity Period Required</label>
                <input value={form.validity_period_required} onChange={(e) => set('validity_period_required', e.target.value)} placeholder="e.g. 30 days" className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
            </div>

            <div className="border-t pt-3">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Vendor Information</h4>
              <VendorPicker onSelect={onVendorSelect} />
              <div className="grid grid-cols-2 gap-3 mt-2">
                <input placeholder="Vendor Company Name" value={form.vendor_company_name} onChange={(e) => set('vendor_company_name', e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md text-sm" />
                <input placeholder="Contact Person" value={form.vendor_contact_person} onChange={(e) => set('vendor_contact_person', e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md text-sm" />
                <input placeholder="Email" value={form.vendor_email} onChange={(e) => set('vendor_email', e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md text-sm" />
                <input placeholder="Phone" value={form.vendor_phone} onChange={(e) => set('vendor_phone', e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
            </div>

            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-700">Scope of Items / Services Requested</h4>
                <button onClick={() => setItems((p) => [...p, emptyItem()])} className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800">
                  <Plus className="h-3.5 w-3.5" /> Add Item
                </button>
              </div>
              <div className="space-y-2">
                {items.map((it, i) => (
                  <div key={i} className="grid grid-cols-12 gap-1.5 items-center">
                    <input placeholder="Description" value={it.description} onChange={(e) => updateItem(i, 'description', e.target.value)} className="col-span-3 px-2 py-1.5 border border-gray-300 rounded text-xs" />
                    <input placeholder="Specification" value={it.specification} onChange={(e) => updateItem(i, 'specification', e.target.value)} className="col-span-3 px-2 py-1.5 border border-gray-300 rounded text-xs" />
                    <input type="number" placeholder="Qty" value={it.qty} onChange={(e) => updateItem(i, 'qty', e.target.value)} className="col-span-1 px-2 py-1.5 border border-gray-300 rounded text-xs" />
                    <input placeholder="Unit" value={it.unit} onChange={(e) => updateItem(i, 'unit', e.target.value)} className="col-span-1 px-2 py-1.5 border border-gray-300 rounded text-xs" />
                    <input type="number" placeholder="Unit Price" value={it.unit_price} onChange={(e) => updateItem(i, 'unit_price', e.target.value)} className="col-span-2 px-2 py-1.5 border border-gray-300 rounded text-xs" />
                    <div className="col-span-1 text-xs text-gray-600 text-right">{Number(it.total_price || 0).toLocaleString()}</div>
                    <button onClick={() => setItems((p) => p.filter((_, idx) => idx !== i))} className="col-span-1 flex justify-center text-red-500 hover:text-red-700">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
              <p className="text-right text-sm font-semibold mt-2">Grand Total: ₦{grandTotal.toLocaleString()}</p>
            </div>

            <div className="border-t pt-3">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Terms (to be filled once vendor responds)</h4>
              <div className="grid grid-cols-3 gap-3">
                <input placeholder="Freight / Shipping Cost" value={form.freight_shipping_cost} onChange={(e) => set('freight_shipping_cost', e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md text-sm" />
                <input placeholder="Warranty Period" value={form.warranty_period} onChange={(e) => set('warranty_period', e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md text-sm" />
                <input placeholder="Lead Time" value={form.lead_time} onChange={(e) => set('lead_time', e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md text-sm" />
                <input placeholder="Payment Terms Offered" value={form.payment_terms_offered} onChange={(e) => set('payment_terms_offered', e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md text-sm" />
                <input type="date" value={form.quote_valid_until} onChange={(e) => set('quote_valid_until', e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-50">
                {saving ? 'Saving...' : 'Create RFQ'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RfqFormModal;
